/**
 * LicenseBanner (DC-1500)
 *
 * Persistent top-of-page banner that warns users about license expiration.
 * Only visible when license status is 'warning', 'read_only', or worse.
 *
 * @module client/components/license/LicenseBanner
 */

import React from 'react';
import type { LicenseInfo } from '../../hooks/useLicenseStatus';

interface LicenseBannerProps {
  license: LicenseInfo;
}

const LicenseBanner: React.FC<LicenseBannerProps> = ({ license }) => {
  const { status, days_remaining } = license;

  // Only show for non-normal states
  if (status === 'valid' || status === 'loading') {
    return null;
  }

  const { message, backgroundColor, color } = getBannerStyle(status, days_remaining);

  return (
    <div
      role="alert"
      style={{
        padding: '8px 16px',
        backgroundColor,
        color,
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: 500,
        zIndex: 9999,
        position: 'relative',
      }}
    >
      {message}
    </div>
  );
};

function getBannerStyle(
  status: string,
  daysRemaining: number | null,
): { message: string; backgroundColor: string; color: string } {
  switch (status) {
    case 'warning':
      return {
        message: `License expires in ${daysRemaining ?? '?'} day(s). Contact your account manager to renew.`,
        backgroundColor: '#fff3cd',
        color: '#856404',
      };

    case 'read_only':
      return {
        message: 'License has expired. System is in read-only mode. Contact your account manager immediately.',
        backgroundColor: '#f8d7da',
        color: '#721c24',
      };

    case 'locked_out':
      return {
        message: 'License has expired beyond the grace period. The system is locked. Contact your account manager.',
        backgroundColor: '#721c24',
        color: '#ffffff',
      };

    case 'missing':
    case 'invalid':
      return {
        message: 'No valid license detected. Please contact your administrator.',
        backgroundColor: '#721c24',
        color: '#ffffff',
      };

    case 'error':
      return {
        message: 'Unable to verify license status.',
        backgroundColor: '#fff3cd',
        color: '#856404',
      };

    default:
      return {
        message: '',
        backgroundColor: 'transparent',
        color: 'inherit',
      };
  }
}

export default LicenseBanner;
